import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@apollo/client";
import moment from "moment";
import {
  Card,
  Typography,
  Button,
  Row,
  Col,
  Statistic,
  Badge,
  Empty,
  Space,
  Descriptions,
  Progress,
  Tag,
} from "antd";

import { projectSubgraphsPath, projectSubgraphsV2Path, subgraphDetailPath, subgraphV2DetailPath } from "../../helpers/paths";
import { GRAPH_NODE_BASEURL, GRAPH_V2_NODE_BASEURL } from "../../config/StaticConfig";
import { CreateSubgraph } from "../modal/CreateSubgraph";
import { SUBGRAPHS_QUERY } from "../../helpers/graphql/subgraph";
import { networkColorList } from "../../helpers/network.color";

interface Project {
  id: string;
  name: string;
  identifier: string;
  enableGraphV2: boolean;
  secret: string;
}

interface Props {
  project: Project;
  cluster: string;
}

interface Subgraph {
  id: string;
  createdAt: string;
  identifier: string;
  cluster: string;
  name: string;
  projectId: string;
  requestCount: number;
  indexingStatus: {
    synced: string;
    chains: [
      {
        network: string;
        latestBlock: {
          number: number;
        };
        earliestBlock: {
          number: number;
        };
        chainHeadBlock: {
          number: number;
        };
      },
    ];
    health: string;
  };
}

const labelColor = {
  color: "grey",
};

export function SubgraphList({ project, cluster }: Props) {
  const { loading, data } = useQuery(SUBGRAPHS_QUERY, {
    variables: { projectId: project.id, cluster },
    pollInterval: 5000,
  });

  const subgraphs = data?.subgraphs;

  const enableGraphV2 = project.enableGraphV2;
  const clusterName = cluster;
  const clusterToggleName = cluster === 'v1' ? 'Switch to v2' : 'Switch to v1';
  const clusterTogglePath = cluster === 'v1' ? projectSubgraphsV2Path(project.id) : projectSubgraphsPath(project.id)

  return (
    <>
      <Card size="small" style={{ marginBottom: "24px" }}>
        <Row gutter={16}>
          <Col flex={1}>
            <Statistic
              className="ant-statistic-small"
              title="Cluster"
              value={clusterName}
              formatter={(value) => {
                return (
                  <Row gutter={16}>
                    <Col flex={1}>
                      <Typography.Text>{value}</Typography.Text>
                    </Col>
                    {enableGraphV2 ? (
                      <Col flex={1}>
                        <Link to={clusterTogglePath}>
                          <Badge count={clusterToggleName} />
                        </Link>
                      </Col>
                    ) : ( <Col flex={1}></Col> )}
                  </Row>
                )
              }}
            />
          </Col>
          <Col flex={1}>
            <Statistic
              className="ant-statistic-small"
              title="Namespace"
              value={project.identifier}
              formatter={(value) => {
                return <Typography.Text>{value}</Typography.Text>;
              }}
            />
          </Col>
          <Col flex={1}>
            <Statistic
              className="ant-statistic-small"
              title="Access Token"
              value={project.secret}
              formatter={(value) => {
                return <Typography.Text copyable>{value}</Typography.Text>;
              }}
            />
          </Col>
          <Col flex={1} style={{ textAlign: "right", lineHeight: "53px", verticalAlign: "middle" }}>
            <CreateSubgraph project={project} cluster={cluster} />
          </Col>
        </Row>
      </Card>
      {loading ? (
        <Card loading={loading} bordered={false} className="card-loading" />
      ) : (
        <>
          {subgraphs && subgraphs.length > 0 ? (
            subgraphs.map((record: Subgraph) => {
              const health = record?.indexingStatus?.health || "not deployed";
              const indexingStatus = record?.indexingStatus;
              const synced = indexingStatus?.synced;
              const chain = indexingStatus?.chains.length > 0 ? indexingStatus?.chains[0] : null;
              const latestBlock = chain?.latestBlock?.number || 0;
              const earliestBlock = chain?.earliestBlock?.number || 0;
              const chainHeadBlock = chain?.chainHeadBlock?.number || 0;
              const networkColor =
                chain?.network !== undefined ? networkColorList[chain?.network] : "grey";

              const status =
                health === "not deployed" ? "warning" : health !== "healthy" ? "error" : "success";

              const syncText =
                synced && health === "healthy"
                  ? "Synced"
                  : health.charAt(0).toUpperCase() + health.slice(1);

              return (
                <Card
                  key={record.id}
                  className={"card-body"}
                  title={record.name}
                  extra={
                    <Link to={record.cluster === 'v1' ? subgraphDetailPath(record.projectId, record.identifier) : subgraphV2DetailPath(record.projectId, record.identifier)}>
                      <Button type="ghost">Details</Button>
                    </Link>
                  }
                >
                  <Descriptions
                    layout="vertical"
                    colon={false}
                    column={{ md: 5, sm: 4, xs: 3 }}
                    size="small"
                  >
                    <Descriptions.Item label="Subgraph ID" labelStyle={labelColor}>
                      <Typography.Text copyable>{record.id}</Typography.Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Syncing Status" labelStyle={labelColor}>
                      {synced || typeof synced === "undefined" ? (
                        <Link to={record.cluster === 'v1' ? subgraphDetailPath(record.projectId, record.identifier) : subgraphV2DetailPath(record.projectId, record.identifier)}>
                          <Button size="small">
                            <Badge status={status} text={syncText} />
                          </Button>
                        </Link>
                      ) : (
                        <Progress
                          style={{ width: "12vw", minWidth: 80 }}
                          strokeColor={{
                            "0%": "#108ee9",
                            "100%": "#87d068",
                          }}
                          status={synced ? "success" : "active"}
                          percent={
                            synced
                              ? 100
                              : Math.floor(
                                  ((latestBlock - earliestBlock) /
                                    (chainHeadBlock - earliestBlock)) *
                                    100,
                                )
                          }
                        />
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Network" labelStyle={labelColor}>
                      <Tag color={networkColor}>{chain?.network || "N/A"}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Requests on last 24 hrs" labelStyle={labelColor}>
                      <Typography.Text>{record.requestCount}</Typography.Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Created" labelStyle={labelColor}>
                      <Typography.Text>{moment(record.createdAt).fromNow()}</Typography.Text>
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              );
            })
          ) : (
            <Empty className="ant-empty-card-hero" description={false}>
              <Space direction="vertical">
                <CreateSubgraph project={project} cluster={cluster} />
                <span>or run:</span>
                <Typography.Text code copyable>
                  {`graph create --access-token=${project.secret} --node ${cluster === 'v1' ? GRAPH_NODE_BASEURL : GRAPH_V2_NODE_BASEURL} ${project.identifier}/first-subgraph`}
                </Typography.Text>
              </Space>
            </Empty>
          )}
        </>
      )}
    </>
  );
}
